import { Species } from '@/types'

/**
 * Kedi ve köpek içerik merkezleri.
 *
 * Buradaki tıbbi bilgiler genel kabul görmüş veteriner hekimlik pratiğine
 * dayanır ve bilgilendirme amaçlıdır. Aşı takvimleri ülkemizde yaygın olarak
 * uygulanan protokollerdir; her hastanın programı muayene sonrası hekim
 * tarafından belirlenir. Sayfalarda fiyat yazılmaz — fiyat muayeneye,
 * kiloya ve anestezi riskine göre değiştiğinden telefonla paylaşılır.
 */
export const species: Species[] = [
  {
    key: 'kedi',
    label: 'Kedi',
    labelPlural: 'Kediler',
    accusative: 'kediyi',
    heroTitle: 'Kedi Sağlığı',
    heroSubtitle:
      'Aşıdan kısırlaştırmaya, parazit korunmasından acil durumlara kadar kedi sahiplerinin en çok sorduğu konular.',
    metaTitle: 'Şanlıurfa Kedi Veteriner',
    metaDescription:
      'Şanlıurfa Karaköprü’de kedi muayenesi, aşı takvimi, kısırlaştırma ve parazit korunması. Kedi sahiplerinin en çok sorduğu konular ve klinik hizmetlerimiz.',
    keywords: [
      'şanlıurfa kedi veteriner', 'kedi veteriner', 'karaköprü kedi veteriner',
      'kedi aşı takvimi', 'kedi kısırlaştırma', 'kedi muayene', 'urfa kedi veteriner'
    ],
    image: '/persian.webp',
    topics: [
      {
        slug: 'asi-takvimi',
        title: 'Kedi Aşı Takvimi',
        metaTitle: 'Kedi Aşı Takvimi: Yavru ve Erişkin',
        description:
          'Yavru kedilerde aşı kaç haftalıkken başlar, karma ve kuduz aşısı ne zaman yapılır? Kedi aşı takvimi, tekrar dozları ve aşı öncesi hazırlık.',
        keywords: [
          'kedi aşı takvimi', 'yavru kedi aşı', 'kedi karma aşı', 'kedi kuduz aşısı',
          'kedi aşısı ne zaman yapılır', 'kedi aşı fiyatları şanlıurfa'
        ],
        intro:
          'Kedilerde aşılama, yavruluk döneminde anneden gelen bağışıklığın azalmaya başladığı ' +
          'haftalarda devreye girer. Doğru zamanda başlanan ve aksatılmadan tamamlanan bir aşı ' +
          'programı, panleukopeni gibi ölümcül seyredebilen hastalıklara karşı en etkili korumadır.',
        sections: [
          {
            heading: 'Yavru kedi aşı programı',
            paragraphs: [
              'Yavru kediler anne sütüyle geçici bir bağışıklık alır. Bu bağışıklık 6-9. haftalarda ' +
              'zayıflamaya başlar ve aşılama tam bu dönemde başlatılır. Tek doz yeterli değildir; ' +
              'koruma, tekrar dozlarıyla oluşur.'
            ],
            table: {
              caption: 'Yavru kedilerde yaygın uygulanan aşı sırası',
              columns: ['Yaş', 'Aşı', 'Açıklama'],
              rows: [
                ['8–9 hafta', 'Karma aşı (1. doz)', 'Panleukopeni, kalisivirüs ve rinotrakeit içerir'],
                ['11–12 hafta', 'Karma aşı (2. doz)', 'İlk dozdan 3–4 hafta sonra tekrarlanır'],
                ['12 hafta ve sonrası', 'Kuduz aşısı', 'Yasal olarak da zorunludur, yıllık tekrarlanır'],
                ['12–16 hafta', 'Lösemi (FeLV) aşısı', 'Dışarı çıkan veya çok kedili ortamdaki kediler için önerilir'],
                ['Yıllık', 'Rapel (tekrar) dozları', 'Karma ve kuduz aşıları her yıl yenilenir']
              ]
            }
          },
          {
            heading: 'Erişkin kedide aşı',
            paragraphs: [
              'Yavruluk programı tamamlanmış kedilerde karma ve kuduz aşıları yılda bir kez ' +
              'tekrarlanır. Aşı geçmişi bilinmeyen sokaktan alınmış bir kedide ise program ' +
              'baştan kurulur: genellikle 3–4 hafta arayla iki doz karma, ardından kuduz uygulanır.'
            ]
          },
          {
            heading: 'Aşı öncesi nelere dikkat edilir?',
            bullets: [
              'Aşı yalnızca sağlıklı kediye yapılır; ateş, ishal veya iştahsızlık varsa ertelenir.',
              'İç ve dış parazit uygulaması aşıdan önce tamamlanmış olmalıdır.',
              'Aşı günü kedinin aç bırakılmasına gerek yoktur.',
              'Aşı sonrası ilk 24 saatte hafif halsizlik normaldir; uzun sürerse bize bildirin.',
              'Aşı karnesi her uygulamada güncellenir ve seyahat, pansiyon, veteriner kontrolü için gereklidir.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Kedi aşısı kaç aylıkken yapılır?',
            answer:
              'İlk karma aşı genellikle 8–9 haftalıkken uygulanır, 3–4 hafta sonra ikinci doz yapılır. ' +
              'Kuduz aşısı 12 haftadan sonra verilir. Kesin program muayenede kedinizin durumuna göre belirlenir.'
          },
          {
            question: 'Eve hiç çıkmayan kediye aşı gerekli mi?',
            answer:
              'Evet. Ev kedileri de sahibin ayakkabı ve kıyafetiyle taşınan etkenlere maruz kalabilir. ' +
              'Ayrıca kuduz aşısı yasal olarak zorunludur. Lösemi aşısı ise dışarı çıkmayan kedilerde ' +
              'muayene sonrası değerlendirilir.'
          },
          {
            question: 'Aşı takvimi aksadıysa baştan mı başlanır?',
            answer:
              'Arada uzun bir boşluk oluştuysa koruma tam gelişmemiş olabilir ve program yenilenebilir. ' +
              'Aşı karnesiyle birlikte gelin, geçmişe bakıp en uygun planı birlikte kuralım.'
          }
        ]
      },
      {
        slug: 'kisirlastirma',
        title: 'Kedi Kısırlaştırma',
        metaTitle: 'Kedi Kısırlaştırma: Yaş ve İyileşme',
        description:
          'Kedi kısırlaştırma kaç aylıkken yapılır, operasyon nasıl geçer, iyileşme ne kadar sürer? ' +
          'Şanlıurfa Karaköprü’de steril ameliyathanede kedi kısırlaştırma.',
        keywords: [
          'kedi kısırlaştırma', 'kedi kısırlaştırma yaşı', 'dişi kedi kısırlaştırma',
          'erkek kedi kısırlaştırma', 'kedi kısırlaştırma sonrası bakım', 'şanlıurfa kedi kısırlaştırma'
        ],
        intro:
          'Kısırlaştırma, istenmeyen yavruların önüne geçmenin yanında kedinin sağlığını da doğrudan ' +
          'etkileyen bir operasyondur. Dişilerde meme tümörü ve rahim iltihabı (piyometra) riskini ' +
          'belirgin biçimde azaltır; erkeklerde idrar işaretleme ve kavga kaynaklı yaralanmaları düşürür.',
        sections: [
          {
            heading: 'Uygun yaş ne zaman?',
            paragraphs: [
              'Genel uygulama 6–8 aylık dönemde, yani ilk kızgınlık öncesinde operasyondur. ' +
              'Dişilerde ilk kızgınlıktan önce yapılan kısırlaştırmanın meme tümörü riskini azaltmada ' +
              'daha etkili olduğu bilinmektedir. Gelişimi geride kalmış veya kronik hastalığı olan ' +
              'kedilerde bu aralık değişebilir; karar muayene ve gerekirse kan tahlili sonrası verilir.'
            ]
          },
          {
            heading: 'Operasyon günü nasıl ilerler?',
            table: {
              caption: 'Kısırlaştırma sürecinin adımları',
              columns: ['Aşama', 'Ne yapılır'],
              rows: [
                ['Ön değerlendirme', 'Genel muayene, gerekirse kan tahlili ve anestezi risk değerlendirmesi'],
                ['Açlık', 'Operasyondan önce genellikle 8 saat aç, su serbest bırakılır'],
                ['Anestezi', 'Kiloya göre hesaplanmış anestezi ve operasyon boyunca takip'],
                ['Operasyon', 'Dişilerde 20–40 dakika, erkeklerde daha kısa sürer'],
                ['Uyanma', 'Klinikte gözlem altında uyanma ve ağrı yönetimi'],
                ['Taburcu', 'Aynı gün taburcu; koruyucu yaka ve ev bakımı anlatılır']
              ]
            }
          },
          {
            heading: 'Operasyon sonrası bakım',
            bullets: [
              'Koruyucu yaka (elizabeth yakası) dikişler kapanana kadar çıkarılmamalıdır.',
              'İlk gece iştahsızlık ve sakinlik normaldir; ertesi gün iştah dönmelidir.',
              'Dikiş yerinde kızarıklık, şişlik, akıntı veya kötü koku varsa hemen aranmalıdır.',
              'Yüksek yerlere zıplaması ilk günlerde engellenmelidir.',
              'Dikişler genellikle 10–14 gün içinde kontrol edilir.'
            ]
          },
          {
            heading: 'Fiyat neye göre değişir?',
            paragraphs: [
              'Kısırlaştırma ücreti sabit değildir; cinsiyet, kilo, yaş, anestezi riski ve gerekli ' +
              'ön tahliller fiyatı belirler. Dişi kedilerde operasyon daha kapsamlı olduğu için ' +
              'erkeklere göre farklılık gösterir. Muayene sonrası size net bir tutar söylüyoruz — ' +
              'telefonda tahmini bilgi de verebiliriz.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Kedi kısırlaştırma kaç aylıkken yapılır?',
            answer:
              'Yaygın uygulama 6–8 aylık dönemdir. Dişilerde ilk kızgınlık öncesinde yapılması tercih edilir. ' +
              'Kedinizin gelişimine göre bu aralık değişebilir, muayenede birlikte karar veriyoruz.'
          },
          {
            question: 'Kısırlaştırma sonrası kedi kilo alır mı?',
            answer:
              'Kısırlaştırma sonrası enerji ihtiyacı azalır. Mama miktarı ayarlanmaz ve hareket ' +
              'desteklenmezse kilo alımı olabilir. Operasyon sonrası beslenme planını birlikte düzenliyoruz.'
          },
          {
            question: 'Operasyon aynı gün mü biter?',
            answer:
              'Evet. Kedi sabah kliniğe alınır, operasyon ve uyanma sonrası aynı gün içinde taburcu edilir. ' +
              'Komplikasyon riski görülen vakalarda gözlem süresi uzatılabilir.'
          }
        ]
      },
      {
        slug: 'parazit-korunmasi',
        title: 'Kedilerde Parazit Korunması',
        metaTitle: 'Kedilerde İç ve Dış Parazit Korunması',
        description:
          'Kedilerde iç ve dış parazit uygulaması ne sıklıkla yapılır? Pire, kene ve bağırsak ' +
          'solucanlarına karşı korunma, belirtiler ve tedavi.',
        keywords: [
          'kedi iç parazit', 'kedi dış parazit', 'kedide pire', 'kedide kene',
          'kedi solucan düşürme', 'kedi parazit ilacı'
        ],
        intro:
          'Parazitler kedilerde yalnızca kaşıntı yapmaz; kansızlık, kilo kaybı ve bazı durumlarda ' +
          'insana da bulaşabilen hastalıklara yol açabilir. Düzenli koruma, tedaviden hem daha ' +
          'kolay hem de daha ucuzdur.',
        sections: [
          {
            heading: 'İç parazitler',
            paragraphs: [
              'Bağırsak solucanları yavru kedilerde anne sütü yoluyla bile geçebilir. Karın şişliği, ' +
              'zayıflama, dışkıda pirinç tanesi görünümünde parçalar ve kusma tipik belirtilerdir. ' +
              'Yavrularda daha sık, erişkinlerde yaşam tarzına göre 3–6 ayda bir uygulama yapılır.'
            ]
          },
          {
            heading: 'Dış parazitler',
            paragraphs: [
              'Pire, kedilerde en sık görülen dış parazittir ve alerjik deri iltihabına yol açabilir. ' +
              'Kene ise özellikle dışarı çıkan kedilerde risklidir. Damla, tablet veya tasma formunda ' +
              'koruyucular kullanılır; ürün seçimi kedinin kilosuna ve yaşam alanına göre yapılır.'
            ],
            bullets: [
              'Köpekler için üretilen bazı pire ürünleri kediler için ZEHİRLİDİR — asla kullanmayın.',
              'Evde bir hayvanda pire varsa diğerlerine de aynı anda uygulama yapılmalıdır.',
              'Pire tedavisinde ortam (yatak, halı, kanepe) da temizlenmelidir.',
              'Kene çıkarmak için alkol veya sıcak nesne kullanmayın; yanlış çıkarma bulaş riskini artırır.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Kedide iç parazit ne sıklıkla yapılmalı?',
            answer:
              'Yavrularda daha sık, erişkin ev kedilerinde genellikle 3–6 ayda bir uygulanır. ' +
              'Dışarı çıkan veya avlanan kedilerde sıklık artırılır. Program muayenede belirlenir.'
          },
          {
            question: 'Kedimde pire var, ev de temizlenmeli mi?',
            answer:
              'Evet. Pirelerin büyük bölümü hayvanın üzerinde değil, ortamda bulunur. Yalnızca kediyi ' +
              'tedavi etmek çoğu zaman yetmez; yatak, halı ve kanepenin de temizlenmesi gerekir.'
          }
        ]
      }
    ]
  },
  {
    key: 'kopek',
    label: 'Köpek',
    labelPlural: 'Köpekler',
    accusative: 'köpeği',
    heroTitle: 'Köpek Sağlığı',
    heroSubtitle:
      'Aşı takviminden kısırlaştırmaya, parazit korunmasından acil durumlara kadar köpek sahiplerinin en çok sorduğu konular.',
    metaTitle: 'Şanlıurfa Köpek Veteriner',
    metaDescription:
      'Şanlıurfa Karaköprü’de köpek muayenesi, aşı takvimi, kısırlaştırma ve parazit korunması. Köpek sahiplerinin en çok sorduğu konular ve klinik hizmetlerimiz.',
    keywords: [
      'şanlıurfa köpek veteriner', 'köpek veteriner', 'karaköprü köpek veteriner',
      'köpek aşı takvimi', 'köpek kısırlaştırma', 'köpek muayene', 'urfa köpek veteriner'
    ],
    image: '/golden-retriever.webp',
    topics: [
      {
        slug: 'asi-takvimi',
        title: 'Köpek Aşı Takvimi',
        metaTitle: 'Köpek Aşı Takvimi: Karma ve Kuduz',
        description:
          'Yavru köpekte aşı kaç haftalıkken başlar, karma aşı kaç doz yapılır, kuduz ne zaman ' +
          'uygulanır? Köpek aşı takvimi ve tekrar dozları.',
        keywords: [
          'köpek aşı takvimi', 'yavru köpek aşı', 'köpek karma aşı', 'köpek kuduz aşısı',
          'parvo aşısı', 'köpek bronşin aşısı'
        ],
        intro:
          'Köpeklerde aşılama, parvovirüs ve gençlik hastalığı (distemper) gibi ölümcül seyredebilen ' +
          'enfeksiyonlara karşı tek gerçek korumadır. Özellikle parvovirüs, aşısız yavrularda çok ' +
          'hızlı ilerler; bu yüzden takvimin aksatılmadan tamamlanması kritiktir.',
        sections: [
          {
            heading: 'Yavru köpek aşı programı',
            paragraphs: [
              'Anneden gelen bağışıklık 6–8. haftalarda azalmaya başlar ve aşılama bu dönemde ' +
              'başlatılır. Yavrunun tam korunması, son doz tamamlandıktan yaklaşık 1–2 hafta ' +
              'sonra oluşur — bu süre dolmadan parka ve sokağa çıkarılması önerilmez.'
            ],
            table: {
              caption: 'Yavru köpeklerde yaygın uygulanan aşı sırası',
              columns: ['Yaş', 'Aşı', 'Açıklama'],
              rows: [
                ['6–8 hafta', 'Karma aşı (1. doz)', 'Distemper, hepatit, parvovirüs ve parainfluenza'],
                ['9–11 hafta', 'Karma aşı (2. doz)', '3–4 hafta arayla tekrarlanır'],
                ['12–14 hafta', 'Karma aşı (3. doz)', 'Leptospiroz bileşeni bu dozda eklenebilir'],
                ['12 hafta ve sonrası', 'Kuduz aşısı', 'Yasal olarak zorunludur, yıllık tekrarlanır'],
                ['8 hafta ve sonrası', 'Bronşin (Bordetella)', 'Pansiyon, eğitim veya kalabalık ortam öncesi önerilir'],
                ['Yıllık', 'Rapel (tekrar) dozları', 'Karma ve kuduz her yıl yenilenir']
              ]
            }
          },
          {
            heading: 'Erişkin köpekte aşı',
            paragraphs: [
              'Yavruluk programı tamamlanan köpeklerde karma ve kuduz aşıları yılda bir tekrarlanır. ' +
              'Aşı geçmişi bilinmeyen sokaktan alınmış köpeklerde program yeniden kurulur.'
            ]
          },
          {
            heading: 'Aşı öncesi dikkat edilecekler',
            bullets: [
              'Aşı yalnızca sağlıklı köpeğe yapılır; ishal, kusma veya ateş varsa ertelenir.',
              'İç ve dış parazit uygulaması aşıdan önce tamamlanmalıdır.',
              'Son doz tamamlanana kadar yavruyu sokağa ve köpek parkına çıkarmayın.',
              'Aşı karnesi seyahat, pansiyon ve eğitim kayıtları için gereklidir.',
              'Aşı sonrası hafif halsizlik normaldir; 24 saatten uzun sürerse bize bildirin.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Yavru köpeğe ilk aşı kaç haftalıkken yapılır?',
            answer:
              'İlk karma aşı genellikle 6–8 haftalıkken uygulanır ve 3–4 hafta arayla toplam 2–3 doz ' +
              'tamamlanır. Kuduz aşısı 12 haftadan sonra verilir.'
          },
          {
            question: 'Aşıları bitmeden köpeğimi dışarı çıkarabilir miyim?',
            answer:
              'Son doz tamamlanıp koruma oluşana kadar (yaklaşık 1–2 hafta) sokak, park ve diğer ' +
              'köpeklerle temastan kaçınmak gerekir. Parvovirüs zeminde uzun süre canlı kalabilir.'
          },
          {
            question: 'Kuduz aşısı zorunlu mu?',
            answer:
              'Evet, ülkemizde köpeklerde kuduz aşısı yasal olarak zorunludur ve her yıl tekrarlanır. ' +
              'Aşı karnesine işlenir.'
          }
        ]
      },
      {
        slug: 'kisirlastirma',
        title: 'Köpek Kısırlaştırma',
        metaTitle: 'Köpek Kısırlaştırma: Yaş ve İyileşme',
        description:
          'Köpek kısırlaştırma kaç aylıkken yapılır, ırka göre değişir mi, iyileşme ne kadar sürer? ' +
          'Şanlıurfa Karaköprü’de steril ameliyathanede köpek kısırlaştırma.',
        keywords: [
          'köpek kısırlaştırma', 'köpek kısırlaştırma yaşı', 'dişi köpek kısırlaştırma',
          'erkek köpek kısırlaştırma', 'köpek kısırlaştırma sonrası bakım'
        ],
        intro:
          'Kısırlaştırma köpeklerde üreme kontrolünün yanında sağlık açısından da belirgin fayda ' +
          'sağlar. Dişilerde rahim iltihabı (piyometra) ve meme tümörü riskini azaltır; erkeklerde ' +
          'prostat sorunları ve bazı davranış problemlerinde iyileşme görülebilir.',
        sections: [
          {
            heading: 'Uygun yaş ırka göre değişir',
            paragraphs: [
              'Küçük ırklarda 6–9 ay yaygın bir aralıkken, büyük ve dev ırklarda iskelet gelişiminin ' +
              'tamamlanması beklenir; bu köpeklerde 12–18 aya kadar uzayabilir. Erken kısırlaştırmanın ' +
              'büyük ırklarda eklem sorunları açısından değerlendirilmesi gerekir. Bu nedenle köpeğinizin ' +
              'ırkı, kilosu ve gelişim durumu operasyon zamanını doğrudan etkiler.'
            ]
          },
          {
            heading: 'Operasyon süreci',
            table: {
              caption: 'Kısırlaştırma sürecinin adımları',
              columns: ['Aşama', 'Ne yapılır'],
              rows: [
                ['Ön değerlendirme', 'Genel muayene, kan tahlili ve anestezi risk değerlendirmesi'],
                ['Açlık', 'Operasyondan önce genellikle 8–12 saat aç, su serbest'],
                ['Anestezi', 'Kiloya göre hesaplanmış anestezi, operasyon boyunca takip'],
                ['Operasyon', 'Dişilerde 30–60 dakika, erkeklerde daha kısa'],
                ['Uyanma', 'Gözlem altında uyanma ve ağrı yönetimi'],
                ['Taburcu', 'Aynı gün taburcu, ev bakımı ve kontrol tarihi anlatılır']
              ]
            }
          },
          {
            heading: 'Operasyon sonrası bakım',
            bullets: [
              'Koruyucu yaka dikişler kapanana kadar çıkarılmamalıdır.',
              'İlk 10 gün koşu, zıplama ve uzun yürüyüşten kaçınılmalıdır.',
              'Tuvalet yürüyüşleri kısa ve tasmalı yapılmalıdır.',
              'Dikiş yerinde şişlik, akıntı veya kötü koku varsa hemen aranmalıdır.',
              'Dikişler genellikle 10–14 gün içinde kontrol edilir.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Köpek kısırlaştırma kaç aylıkken yapılmalı?',
            answer:
              'Küçük ırklarda genellikle 6–9 ay, büyük ırklarda iskelet gelişimi tamamlandıktan sonra ' +
              '12–18 ay arası tercih edilir. Köpeğinizin ırkına ve gelişimine göre muayenede karar veriyoruz.'
          },
          {
            question: 'Kısırlaştırma köpeğin karakterini değiştirir mi?',
            answer:
              'Temel karakter değişmez. Hormona bağlı bazı davranışlarda (işaretleme, kaçma isteği, ' +
              'bazı saldırganlık biçimleri) azalma görülebilir. Öğrenilmiş davranışlar için eğitim gerekir.'
          },
          {
            question: 'Operasyon öncesi kan tahlili şart mı?',
            answer:
              'Anestezi güvenliği için önerilir. Karaciğer ve böbrek değerleri anestezi protokolünü ' +
              'doğrudan etkiler; özellikle yaşlı köpeklerde tahlil yapılmadan operasyona girilmez.'
          }
        ]
      },
      {
        slug: 'parazit-korunmasi',
        title: 'Köpeklerde Parazit Korunması',
        metaTitle: 'Köpeklerde İç ve Dış Parazit Korunması',
        description:
          'Köpeklerde iç ve dış parazit uygulaması ne sıklıkla yapılır? Pire, kene, kalp kurdu ve ' +
          'bağırsak solucanlarına karşı korunma.',
        keywords: [
          'köpek iç parazit', 'köpek dış parazit', 'köpekte pire', 'köpekte kene',
          'köpek kalp kurdu', 'köpek solucan düşürme'
        ],
        intro:
          'Şanlıurfa iklimi kene ve tatarcık aktivitesinin yüksek olduğu bir bölgedir. Bu da ' +
          'köpeklerde kene kaynaklı hastalıklar ve leishmaniasis açısından koruyucu uygulamaları ' +
          'daha da önemli hale getirir.',
        sections: [
          {
            heading: 'Dış parazitler ve bölgesel risk',
            paragraphs: [
              'Kene, ehrlichiosis ve babesiosis gibi kan parazitlerini taşır. Tatarcık sineği ise ' +
              'leishmaniasis etkenini bulaştırır ve bölgemizde görülen önemli bir hastalıktır. ' +
              'Bu nedenle sıcak aylarda düzenli dış parazit koruması yalnızca konfor değil, ' +
              'gerçek bir sağlık önlemidir.'
            ],
            bullets: [
              'Kene koruması özellikle ilkbahar–sonbahar arası aksatılmamalıdır.',
              'Tatarcığa karşı koruyucu tasma veya damla uygulamaları önerilir.',
              'Keneyi yanlış çıkarmak bulaş riskini artırır; kliniğe getirin veya doğru yöntemi öğrenin.',
              'Kene tutunmasından sonra halsizlik, ateş, burun kanaması varsa hemen muayene gerekir.'
            ]
          },
          {
            heading: 'İç parazitler',
            paragraphs: [
              'Bağırsak solucanları yavrularda gelişim geriliğine, erişkinlerde zayıflama ve ishale ' +
              'yol açar. Kalp kurdu (dirofilariasis) ise sivrisinekle bulaşır ve kalpte yerleşerek ' +
              'ciddi hasar oluşturur; korunma tedaviden çok daha kolaydır. Uygulama sıklığı köpeğin ' +
              'yaşam tarzına göre 3–6 ayda birdir.'
            ]
          }
        ],
        faqs: [
          {
            question: 'Köpekte kene ilacı ne sıklıkla uygulanır?',
            answer:
              'Ürün tipine göre değişir: damla genellikle aylık, bazı tabletler 1–3 aylık koruma sağlar. ' +
              'Bölgemizde kene aktivitesi yüksek olduğu için sıcak aylarda aksatılmaması önemlidir.'
          },
          {
            question: 'Kalp kurdu Şanlıurfa’da görülür mü?',
            answer:
              'Sivrisineğin bulunduğu her bölgede risk vardır. Korunma için düzenli uygulama yapılır; ' +
              'şüphe durumunda kan testiyle kontrol edilir.'
          }
        ]
      }
    ]
  }
]

export const findSpecies = (key?: string) => species.find(s => s.key === key)

export const findTopic = (speciesKey?: string, topicSlug?: string) => {
  const sp = findSpecies(speciesKey)
  if (!sp) return undefined
  const topic = sp.topics.find(t => t.slug === topicSlug)
  return topic ? { species: sp, topic } : undefined
}
